/** Durée d'une fenêtre de mesure : les chiffres affichés sont ceux de la dernière fenêtre close. */
export const RENDER_STATS_WINDOW_MS = 1000;

export interface FrameTimings {
  /** Images rendues par seconde. */
  frames: number;
  /** Images de la fenêtre : de quoi ramener un total compté (`count`) à une moyenne par image. */
  count: number;
  averageMs: number;
  maxMs: number;
}

/**
 * Temps passés par image et compteurs d'évènements, agrégés par fenêtre d'une seconde : un chiffre
 * qui change à chaque image ne se lit pas, et un maximum sur toute la session ne dit rien du moment.
 */
export class RenderStatsWindow {
  private start: number;
  private durations: number[] = [];
  private counts = new Map<string, number>();
  private last: { timings: FrameTimings; counts: Record<string, number> } = { timings: { frames: 0, count: 0, averageMs: 0, maxMs: 0 }, counts: {} };

  constructor(now: number) {
    this.start = now;
  }

  recordFrame(durationMs: number, now: number): void {
    this.roll(now);
    this.durations.push(durationMs);
  }

  count(name: string, now: number, by = 1): void {
    this.roll(now);
    this.counts.set(name, (this.counts.get(name) ?? 0) + by);
  }

  snapshot(now: number): { timings: FrameTimings; counts: Record<string, number> } {
    this.roll(now);
    return this.last;
  }

  private roll(now: number): void {
    const elapsed = now - this.start;
    if (elapsed < RENDER_STATS_WINDOW_MS) return;
    const seconds = elapsed / 1000;
    const total = this.durations.reduce((sum, duration) => sum + duration, 0);
    this.last = {
      // Au-delà de deux fenêtres sans rien, la carte était immobile : rien ne s'est passé « par seconde ».
      timings: {
        frames: elapsed > 2 * RENDER_STATS_WINDOW_MS ? 0 : this.durations.length / seconds,
        count: this.durations.length,
        averageMs: this.durations.length ? total / this.durations.length : 0,
        maxMs: this.durations.length ? Math.max(...this.durations) : 0,
      },
      counts: Object.fromEntries([...this.counts].map(([name, value]) => [name, value])),
    };
    this.durations = [];
    this.counts.clear();
    this.start = now;
  }
}
